// src/features/equipment/components/maintenance/MaintenanceTimeline.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Link } from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import { timelineItemClasses } from '@mui/lab/TimelineItem';
import {
  ReportProblem as ReportProblemIcon,
  Build as BuildIcon,
  Comment as CommentIcon,
  Edit as EditIcon,
  AttachFile as AttachFileIcon,
  LinkOff as LinkOffIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';

import ProfilePicture from '../../../../components/common/ProfilePicture';
import { useFormatDateTime } from './MaintenanceStatusChip';

const ENTRY_CONFIG = {
  reported: { icon: ReportProblemIcon, color: 'warning' },
  pulled: { icon: BuildIcon, color: 'info' },
  note: { icon: CommentIcon, color: 'grey' },
  updated: { icon: EditIcon, color: 'grey' },
  file_added: { icon: AttachFileIcon, color: 'grey' },
  file_removed: { icon: LinkOffIcon, color: 'grey' },
  completed: { icon: CheckCircleIcon, color: 'success' },
  cancelled: { icon: CancelIcon, color: 'error' },
};

function StatusChange({ entry }) {
  const { t } = useTranslation(['equipment']);
  const before = entry.before_values?.equipment_status;
  const after = entry.after_values?.equipment_status;
  if (!before || !after || before === after) return null;
  return (
    <Typography variant="caption" color="text.secondary" display="block">
      {t('equipment:maintenance.timeline.status_change', {
        from: t(`equipment:status.${before}`),
        to: t(`equipment:status.${after}`),
      })}
    </Typography>
  );
}

function ChangedFields({ entry }) {
  const { t } = useTranslation(['equipment']);
  if (entry.entry_type !== 'updated' || !entry.after_values) return null;
  // The contact link travels with the technician's name, which is shown instead
  return Object.entries(entry.after_values).filter(([field]) => field !== 'service_provider_contact_id').map(([field, value]) => (
    <Typography key={field} variant="caption" color="text.secondary" display="block">
      {t(`equipment:maintenance.fields.${field}`)}: {value || '–'}
    </Typography>
  ));
}

/** Links between a pulled case and the fault reports its completion closed. */
function ResolvedLinks({ entry, onOpenCase }) {
  const { t } = useTranslation(['equipment']);
  const caseLink = (ref) => (onOpenCase
    ? <Link component="button" type="button" variant="caption"
            sx={{ verticalAlign: 'baseline' }} onClick={() => onOpenCase(ref.maintenance_id)}>{ref.title}</Link>
    : ref.title);
  const withCase = entry.after_values?.resolved_with;
  const reports = entry.after_values?.resolved_reports || [];

  if (withCase) {
    return (
      <Typography variant="caption" color="text.secondary" display="block">
        {t('equipment:maintenance.timeline.resolved_with')} {caseLink(withCase)}
      </Typography>
    );
  }
  if (!reports.length) return null;
  return (
    <Typography variant="caption" color="text.secondary" display="block">
      {t('equipment:maintenance.timeline.resolved_reports')}{' '}
      {reports.map((ref, i) => <React.Fragment key={ref.maintenance_id}>{i > 0 && ', '}{caseLink(ref)}</React.Fragment>)}
    </Typography>
  );
}

// Closing a repair reads differently from a fault that was fixed on the spot
const entryLabelKey = (entry) => (
  entry.entry_type === 'completed' && entry.before_values?.equipment_status === 'in_maintenance'
    ? 'completed_repair'
    : entry.entry_type
);

export default function MaintenanceTimeline({ entries, onOpenCase }) {
  const { t } = useTranslation(['equipment']);
  const formatDateTime = useFormatDateTime();

  return (
    <Timeline sx={{ p: 0, m: 0, [`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 } }}>
      {entries.map((entry, index) => {
        const config = ENTRY_CONFIG[entry.entry_type] || ENTRY_CONFIG.note;
        const Icon = config.icon;
        return (
          <TimelineItem key={entry.log_id} sx={{ minHeight: 'auto' }}>
            <TimelineSeparator>
              <TimelineDot color={config.color} variant={config.color === 'grey' ? 'outlined' : 'filled'} sx={{ my: 2 }}>
                <Icon sx={{ fontSize: 16 }} />
              </TimelineDot>
              {index < entries.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ py: 2, pr: 0 }}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="body2" fontWeight={600}>
                  {t(`equipment:maintenance.timeline.${entryLabelKey(entry)}`)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(entry.created_at)}
                </Typography>
              </Box>
              {entry.message && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {entry.message}
                </Typography>
              )}
              <StatusChange entry={entry} />
              <ResolvedLinks entry={entry} onOpenCase={onOpenCase} />
              <ChangedFields entry={entry} />
              {entry.staff_member && (
                <Box display="flex" alignItems="center" gap={0.75} mt={0.5}>
                  <ProfilePicture
                    profilePictureFileId={entry.staff_member.profile_picture_file_id}
                    name={entry.staff_member.name}
                    bgcolor={entry.staff_member.color || undefined}
                    size={20}
                    imageSize="32x32"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {entry.staff_member.name}
                  </Typography>
                </Box>
              )}
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
}
